import ExcelJS from 'exceljs';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { escapeHtml } from '@/utils/htmlEscape';

export const useDataExport = () => {
  const exportToCSV = async (data: any[], filename: string, columns?: string[]) => {
    try {
      // If columns specified, filter data
      const exportData = columns 
        ? data.map(row => {
            const filtered: any = {};
            columns.forEach(col => {
              filtered[col] = row[col];
            });
            return filtered;
          })
        : data;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Data');

      if (exportData.length > 0) {
        // Add headers
        const headers = Object.keys(exportData[0]);
        worksheet.addRow(headers);

        // Add data rows
        exportData.forEach(row => {
          worksheet.addRow(headers.map(h => row[h]));
        });
      }
      
      const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
      
      // Generate CSV buffer
      const buffer = await workbook.csv.writeBuffer();
      const blob = new Blob([buffer], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}_${timestamp}.csv`;
      link.click();
      
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} records`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const exportToExcel = async (data: any[], filename: string, sheetName = 'Data') => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(sheetName);

      if (data.length > 0) {
        // Add headers
        const headers = Object.keys(data[0]);
        worksheet.addRow(headers);

        // Style header row
        worksheet.getRow(1).font = { bold: true };
        worksheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE0E0E0' }
        };

        // Add data rows
        data.forEach(row => {
          worksheet.addRow(headers.map(h => row[h]));
        });

        // Auto-width columns
        worksheet.columns.forEach(column => {
          let maxLength = 10;
          column.eachCell?.({ includeEmpty: true }, cell => {
            const cellLength = cell.value ? cell.value.toString().length : 0;
            if (cellLength > maxLength) {
              maxLength = cellLength;
            }
          });
          column.width = Math.min(maxLength + 2, 50);
        });
      }
      
      const timestamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
      
      // Generate Excel buffer
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}_${timestamp}.xlsx`;
      link.click();
      
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} records`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  const printTable = (elementId: string, title: string) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }

    const element = document.getElementById(elementId);
    if (!element) {
      toast.error('Content not found');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(title)}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #000; padding: 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            h1 { margin-bottom: 10px; }
            .header { margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #000; font-size: 12px; text-align: center; }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${title}</h1>
            <p>Generated: ${format(new Date(), 'MMMM d, yyyy h:mm a')}</p>
          </div>
          ${element.innerHTML}
          <div class="footer">
            <p>Confidential - For Internal Use Only</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return {
    exportToCSV,
    exportToExcel,
    printTable
  };
};
